// src/components/SEO/SEO.jsx
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'ExploitEye';
const SITE_URL = 'https://exploiteye.in';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = '@exploiteye';

/**
 * Drop this into any page with page-specific title/description/etc.
 * Renders standard meta tags, Open Graph, Twitter Card, canonical URL,
 * and optional JSON-LD structured data for rich search results.
 *
 * Usage:
 *   <SEO
 *     title="Web & App Development Services"
 *     description="ExploitEye builds secure, scalable web apps, mobile apps, and cybersecurity solutions for ambitious businesses across India."
 *     path="/services"
 *     keywords="web development company India, app development agency, cybersecurity consulting, VAPT services"
 *   />
 */
export default function SEO({
  title,
  description,
  path = '/',
  keywords,
  image = DEFAULT_IMAGE,
  type = 'website',
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Secure Web & App Development Solution in India`;
  const canonical = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* Structured data (JSON-LD) — pass a JS object, it gets stringified */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export { SITE_NAME, SITE_URL, DEFAULT_IMAGE };